import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { signIn } from '../../features/auth/authSlice'
import { ACCESS_TOKEN, REDIRECT_AFTER_LOGIN, REFRESH_TOKEN, StorageSetItem, USER } from "../../lib/storage"
import { sameOriginPath } from "../../lib/safeRedirect"
import { showSnackBar } from "../../components/Snackbar/Snackbar"
import { setSentryUser } from "../../lib/sentryUser"
import { noticeFor } from "../../lib/signInNotices"

const SignIn = () => {
  const dispatch = useDispatch()
  const [reqError, setReqError] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const notice = noticeFor(searchParams.get('notice'))
    if (!notice) return

    showSnackBar(notice.severity, notice.text)
    // Through the router, not history.replaceState, so react-router's location stays authoritative
    // and a reload does not repeat the message. See noticeFor: a key travels, never text.
    const rest = new URLSearchParams(searchParams)
    rest.delete('notice')
    setSearchParams(rest, { replace: true })
  }, [searchParams, setSearchParams])

  const handleSignIn = async () => {
    const credentials = { email, password }
    const response = await dispatch(signIn(credentials))

    if (signIn.fulfilled.match(response)) {
      const data = response.payload
      await saveTokens(data.data)
    } else if (response.error) {
      setReqError(response.payload)
    } else if (signIn.rejected.match(response)) {
      const error = response.error
      console.error(`Sign-in failed: ${error}`)
    }
  }

  useEffect(() => {
    const handleReqError = () => {
      reqError.length > 0 && showSnackBar("error", reqError)
      setReqError('')
    }
    handleReqError()
  }, [reqError])

  const saveTokens = async ({ accessToken, refreshToken, user }) => {
    await StorageSetItem(ACCESS_TOKEN, accessToken)
    await StorageSetItem(REFRESH_TOKEN, refreshToken)
    await StorageSetItem(USER, user)
    setSentryUser(user)

    const redirectUrl = sameOriginPath(localStorage.getItem(REDIRECT_AFTER_LOGIN))
    localStorage.removeItem(REDIRECT_AFTER_LOGIN)
    if (redirectUrl) {
      window.location.href = redirectUrl
    } else {
      window.location.reload(false)
    }
  }

  useEffect(() => {
    const link = document.createElement('link')
    link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=DM+Sans:wght@300;400;500&display=swap'
    link.rel = 'stylesheet'
    document.head.appendChild(link)
    return () => document.head.removeChild(link)
  }, [])

  return (
    <>
      <style>{`
        .sl-root *, .sl-root *::before, .sl-root *::after {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }

        .sl-root {
          font-family: 'DM Sans', sans-serif;
          background: #0f0f0f;
          color: #f0ede8;
          min-height: 100vh;
          display: flex;
          overflow: hidden;
        }

        .sl-left {
          flex: 1;
          position: relative;
          overflow: hidden;
        }

        .sl-left-bg {
          width: 100%;
          height: 100%;
          background: linear-gradient(160deg, #0a0a0a 0%, #1c1508 50%, #0f0f0f 100%);
          position: absolute;
          inset: 0;
        }

        .sl-left svg.sl-grid {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          opacity: 0.06;
        }

        .sl-left svg.sl-accent-line {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          opacity: 0.12;
        }

        .sl-left-glow {
          position: absolute;
          bottom: -100px;
          left: -100px;
          width: 500px;
          height: 500px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(201,169,110,0.12) 0%, transparent 70%);
          pointer-events: none;
        }

        .sl-left-text {
          position: absolute;
          bottom: 56px;
          left: 52px;
          right: 52px;
        }

        .sl-left-text .sl-tag {
          display: inline-block;
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #c9a96e;
          border: 1px solid rgba(201,169,110,0.35);
          padding: 5px 12px;
          border-radius: 100px;
          margin-bottom: 20px;
        }

        .sl-left-text h2 {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(32px, 3.5vw, 52px);
          font-weight: 300;
          line-height: 1.15;
          color: #f0ede8;
          margin-bottom: 16px;
        }

        .sl-left-text p {
          font-size: 13px;
          font-weight: 300;
          color: rgba(240,237,232,0.45);
          line-height: 1.7;
          max-width: 340px;
        }

        .sl-right {
          width: 480px;
          min-width: 420px;
          background: #161616;
          display: flex;
          flex-direction: column;
          padding: 0 52px 52px;
          position: relative;
          border-left: 1px solid rgba(255,255,255,0.07);
        }

        .sl-right::before {
          content: '';
          position: absolute;
          top: -120px;
          right: -120px;
          width: 400px;
          height: 400px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(201,169,110,0.2) 0%, transparent 70%);
          pointer-events: none;
        }

        .sl-logo-area {
          padding: 40px 0 0;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .sl-logo-text {
          display: flex;
          flex-direction: column;
          line-height: 1;
        }

        .sl-logo-text span:first-child {
          font-family: 'Cormorant Garamond', serif;
          font-size: 16px;
          font-weight: 500;
          letter-spacing: 0.05em;
          color: #f0ede8;
        }

        .sl-logo-text span:last-child {
          font-size: 9px;
          font-weight: 400;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(240,237,232,0.45);
          margin-top: 2px;
        }

        .sl-portal-badge {
          position: absolute;
          top: 40px;
          right: 52px;
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 10px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: rgba(240,237,232,0.45);
        }

        .sl-badge-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #c9a96e;
          box-shadow: 0 0 6px #c9a96e;
          animation: sl-pulse 2s infinite;
        }

        @keyframes sl-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }

        .sl-form-area {
          flex: 1;
          display: flex;
          flex-direction: column;
          animation: sl-fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1);
        }

        @keyframes sl-fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .sl-form-header {
          margin-top: 56px;
          margin-bottom: 36px;
        }

        .sl-portal-label {
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #c9a96e;
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .sl-portal-label::before {
          content: '';
          display: inline-block;
          width: 18px;
          height: 1px;
          background: currentColor;
        }

        .sl-form-header h1 {
          font-family: 'Cormorant Garamond', serif;
          font-size: 38px;
          font-weight: 400;
          letter-spacing: -0.02em;
          line-height: 1.1;
          color: #f0ede8;
        }

        .sl-form-header p {
          margin-top: 10px;
          font-size: 13px;
          font-weight: 300;
          color: rgba(240,237,232,0.45);
          line-height: 1.6;
        }

        .sl-field {
          margin-bottom: 18px;
        }

        .sl-field label {
          display: block;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: rgba(240,237,232,0.45);
          margin-bottom: 8px;
        }

        .sl-input-wrap {
          position: relative;
        }

        .sl-input {
          width: 100%;
          padding: 14px 18px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          color: #f0ede8;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          font-weight: 300;
          outline: none;
          transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;
          -webkit-appearance: none;
        }

        .sl-input::placeholder {
          color: rgba(240,237,232,0.2);
        }

        .sl-input:focus {
          border-color: rgba(201,169,110,0.45);
          background: rgba(201,169,110,0.04);
          box-shadow: 0 0 0 3px rgba(201,169,110,0.08);
        }

        .sl-input-password {
          padding-right: 48px;
        }

        .sl-eye-btn {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: rgba(240,237,232,0.45);
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          transition: color 0.2s;
        }

        .sl-eye-btn:hover {
          color: #f0ede8;
        }

        .sl-forgot {
          text-align: right;
          margin-top: -10px;
          margin-bottom: 28px;
        }

        .sl-forgot a {
          font-size: 12px;
          color: rgba(240,237,232,0.45);
          text-decoration: none;
          transition: color 0.2s;
          letter-spacing: 0.02em;
        }

        .sl-forgot a:hover {
          color: #f0ede8;
        }

        .sl-btn-login {
          width: 100%;
          padding: 15px;
          border: none;
          border-radius: 10px;
          background: #c9a96e;
          color: #0f0f0f;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px;
          font-weight: 500;
          letter-spacing: 0.06em;
          cursor: pointer;
          transition: background 0.3s, box-shadow 0.3s, transform 0.15s;
        }

        .sl-btn-login:hover {
          background: #d4b47a;
          box-shadow: 0 8px 24px rgba(201,169,110,0.3);
          transform: translateY(-1px);
        }

        .sl-btn-login:active {
          transform: translateY(0);
        }

        .sl-divider {
          display: flex;
          align-items: center;
          gap: 14px;
          margin: 28px 0;
          color: rgba(240,237,232,0.45);
          font-size: 11px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        .sl-divider::before, .sl-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: rgba(255,255,255,0.07);
        }

        .sl-help-text {
          text-align: center;
          font-size: 12px;
          color: rgba(240,237,232,0.45);
          line-height: 1.6;
        }

        .sl-help-text a {
          color: #c9a96e;
          text-decoration: none;
          font-weight: 500;
        }

        .sl-help-text a:hover {
          opacity: 0.8;
        }

        @media (max-width: 800px) {
          .sl-left { display: none; }
          .sl-right { width: 100%; min-width: unset; }
        }
      `}</style>

      <div className="sl-root">
        <div className="sl-left">
          <div className="sl-left-bg" />
          <div className="sl-left-glow" />
          <svg className="sl-grid" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="sl-grid-pat" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="white" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#sl-grid-pat)" />
          </svg>
          <svg className="sl-accent-line" xmlns="http://www.w3.org/2000/svg">
            <line x1="0" y1="100%" x2="100%" y2="0" stroke="#c9a96e" strokeWidth="1" />
            <line x1="-5%" y1="105%" x2="95%" y2="-5%" stroke="#c9a96e" strokeWidth="0.5" />
          </svg>
          <div className="sl-left-text">
            <span className="sl-tag">Client Portal</span>
            <h2>Your case,<br />our priority.</h2>
            <p>Track your debt defense case, access documents, and stay connected with your legal team from anywhere.</p>
          </div>
        </div>

        <div className="sl-right">
          <div className="sl-logo-area">
            <img src={require('../../assets/images/logo.png')} alt="MDL" style={{ width: 36, height: 36, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
            <div className="sl-logo-text">
              <span>My Debt Lawyers</span>
              <span>Legal Services</span>
            </div>
          </div>

          <div className="sl-portal-badge">
            <span className="sl-badge-dot" />
            <span>Client Portal</span>
          </div>

          <div className="sl-form-area">
            <div className="sl-form-header">
              <div className="sl-portal-label">Client Access</div>
              <h1>Your case,<br />your control.</h1>
              <p>Sign in to track your case progress and communicate with your legal team.</p>
            </div>

            <div className="sl-field">
              <label htmlFor="sl-email">Email address</label>
              <input
                id="sl-email"
                className="sl-input"
                type="email"
                placeholder="your@email.com"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="sl-field">
              <label htmlFor="sl-password">Password</label>
              <div className="sl-input-wrap">
                <input
                  id="sl-password"
                  className={`sl-input${showPassword ? '' : ' sl-input-password'}`}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="sl-eye-btn"
                  tabIndex={-1}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="sl-forgot">
              <a href="/forgot-password">Forgot password?</a>
            </div>

            <button type="button" className="sl-btn-login" onClick={handleSignIn}>
              Access My Case Portal
            </button>

            <div className="sl-divider">new client</div>

            <div className="sl-help-text">
              Don't have an account? <a href="mailto:support@mydebtlawyers.com">Contact us to get started</a>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default SignIn
